import { useState } from "react";
import { useNavigate } from "react-router";
import { Logo } from "../components/Logo";
import { ArrowRight } from "lucide-react";

interface FormData {
  firstName:    string;
  lastName:     string;
  email:        string;
  phone:        string;
  isBusiness:   boolean;
  businessName: string;
}

interface FieldErrors {
  firstName?:    string;
  lastName?:     string;
  email?:        string;
  phone?:        string;
  businessName?: string;
}

export function SecureAccessPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormData>({
    firstName: "", lastName: "", email: "", phone: "",
    isBusiness: false, businessName: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});

  const validate = (): boolean => {
    const e: FieldErrors = {};
    if (!form.firstName.trim())    e.firstName    = "First name is required.";
    if (!form.lastName.trim())     e.lastName     = "Last name is required.";
    if (!form.email.trim())        e.email        = "Email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.phone.trim())        e.phone        = "Phone number is required.";
    if (form.isBusiness && !form.businessName.trim()) e.businessName = "Business name is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validate()) {
      navigate("/nda", { state: form });
    }
  };

  const textField = (
    id: keyof Pick<FormData, "firstName" | "lastName" | "email" | "phone" | "businessName">,
    label: string,
    type = "text",
    placeholder = ""
  ) => (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-[#364153]">
        {label} <span className="text-[#7B1A1A]">*</span>
      </label>
      <input
        id={id}
        type={type}
        value={form[id] as string}
        onChange={(e) => {
          setForm((p) => ({ ...p, [id]: e.target.value }));
          if (errors[id]) setErrors((p) => ({ ...p, [id]: undefined }));
        }}
        placeholder={placeholder}
        className={`w-full px-4 py-2.5 border rounded text-sm text-[#101828] bg-white placeholder-[#CCCCCC] focus:outline-none focus:ring-2 focus:ring-[#7B1A1A]/30 focus:border-[#7B1A1A] transition-colors ${
          errors[id] ? "border-red-400" : "border-[#E5E7EB]"
        }`}
      />
      {errors[id] && <p className="text-xs text-red-500">{errors[id]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2">
          <Logo className="w-14" />
          <span className="text-sm font-medium text-[#101828]">Leo Group</span>
        </button>
        <span className="text-xs text-[#4A5565]">Request Access</span>
      </nav>

      {/* Body */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full bg-[#7B1A1A] text-white text-xs flex items-center justify-center font-semibold">1</span>
              <span className="text-sm font-medium text-[#7B1A1A]">Your Details</span>
            </div>
            <div className="flex-1 h-px bg-[#E5E7EB]" />
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full bg-[#E5E7EB] text-[#4A5565] text-xs flex items-center justify-center font-semibold">2</span>
              <span className="text-sm text-[#4A5565]">NDA & Acceptance</span>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8">
            <h1 className="text-2xl font-semibold text-[#101828] mb-2">Request Access</h1>
            <p className="text-sm text-[#4A5565] mb-8">
              Complete the form below to begin the access request process. All fields are required.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {textField("firstName", "First Name", "text", "John")}
                {textField("lastName",  "Last Name",  "text", "Smith")}
              </div>
              {textField("email", "Email Address", "email", "john@company.com")}
              {textField("phone", "Phone Number",  "tel",   "+27 000 000 0000")}

              {/* Business toggle */}
              <div className="space-y-1.5">
                <p className="block text-sm font-medium text-[#364153]">Representing a Business?</p>
                <div className="flex gap-3">
                  {([false, true] as const).map((val) => (
                    <button
                      key={String(val)}
                      type="button"
                      onClick={() => {
                        setForm((p) => ({ ...p, isBusiness: val, businessName: val ? p.businessName : "" }));
                        if (!val) setErrors((p) => ({ ...p, businessName: undefined }));
                      }}
                      className={`flex-1 py-2.5 rounded border text-sm font-medium transition-colors ${
                        form.isBusiness === val
                          ? "bg-[#7B1A1A] border-[#7B1A1A] text-white"
                          : "bg-white border-[#E5E7EB] text-[#364153] hover:border-[#7B1A1A]/40"
                      }`}
                    >
                      {val ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional business name */}
              {form.isBusiness && (
                <div className="space-y-1.5 transition-all">
                  {textField("businessName", "Business Name", "text", "Acme (Pty) Ltd")}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] text-white py-3 rounded font-medium transition-colors flex items-center justify-center gap-2 mt-2"
              >
                Continue to NDA
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <p className="text-xs text-[#4A5565] text-center mt-6">
              By continuing you agree to sign a Non-Disclosure Agreement.
              Access is granted at LEO Group's sole discretion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
